// src/finances/PriceBook.js
// PRICE BOOK — search every line item across all booked receipts: what you
// actually paid (ex-tax), the item's prorated tax share, when, from which
// vendor, and where the cost landed. Click an item for its price history.
// Right-click a row: See Original Receipt / Add-Edit Alias / Edit Items.
// Column headers click-sort in three states (asc → desc → default newest).
// All data comes exclusively from your own uploaded receipts.
import { API_BASE } from '../apiBase';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import { Search, ArrowLeft, TrendingUp } from 'lucide-react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const authH = () => ({ headers: { Authorization: localStorage.getItem('token') } });
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');
const normName = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
// SKUs compare format-insensitively ("1006-232-907" ≡ "1006232907") but
// display as printed — the dashed variant wins when one has been seen.
const normSku = (s) => String(s || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
// Identity key for ONE product: name + normalized SKU. Vendors (Home Depot
// especially) reuse one display name across different products — only the
// SKU separates a raintight connector from a coupling, or a 3-pack from a
// 5-pack — so grouping must never rely on name alone.
const keyOf = (r) => `${normName(r.name)}|${normSku(r.sku)}`;
// Prettiest display form of a SKU across a product's purchases: prefer a
// variant with separators (dashes), newest otherwise. Deterministic — no AI.
const prettySku = (rs) => {
  let best = '';
  rs.forEach((r) => {
    if (!r.sku) return;
    if (!best) best = r.sku;
    else if (!/[^a-zA-Z0-9]/.test(best) && /[^a-zA-Z0-9]/.test(r.sku)) best = r.sku;
    else if (/[^a-zA-Z0-9]/.test(r.sku) === /[^a-zA-Z0-9]/.test(best)) best = r.sku; // later (newer) wins ties
  });
  return best;
};

// Deterministic pack-size guess from the vendor's name: "(5-Pack)", "5PK",
// "5 pack", "5-count"… → 5. Only a PRE-FILL — the user confirms per member.
const codeKind = (code) => {
  const d = String(code || '').replace(/[^0-9]/g, '');
  if (d.length === 12 && d === String(code || '').replace(/[^0-9a-zA-Z]/g, '')) return 'UPC';
  if (d.length === 13 && d === String(code || '').replace(/[^0-9a-zA-Z]/g, '')) return 'EAN';
  return 'SKU';
};

const guessPack = (name) => {
  const m = /(\d+)\s*[-\s]?(?:pk|pack|packs|count|ct)\b/i.exec(String(name || ''));
  const n = m ? parseInt(m[1], 10) : 1;
  return n >= 1 && n <= 500 ? n : 1;
};

// Catalog columns — one row per PRODUCT (named, or not-yet-named vendor item).
const COLS = [
  { key: 'title', label: 'Product', type: 'text' },
  { key: 'sku', label: 'Item Code', type: 'text' },
  { key: 'vendors', label: 'Vendor(s)', type: 'text' },
  { key: 'buys', label: 'Buys', type: 'num', right: true },
  { key: 'latestUnit', label: 'Latest unit', type: 'num', right: true },
  { key: 'avgUnit', label: 'Avg unit', type: 'num', right: true },
  { key: 'latestDate', label: 'Last purchase', type: 'num' },
];

function PriceBook() {
  // Last search survives navigation (localStorage) — come back and your
  // term re-runs and the same list is waiting.
  const [q, setQ] = useState(() => localStorage.getItem('priceBook.q') || '');
  useEffect(() => { localStorage.setItem('priceBook.q', q); }, [q]);
  const [rows, setRows] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selected, setSelected] = useState(null); // normalized item name → analytics view
  const debRef = useRef(null);

  const forLabel = useCallback((r) => r.project ? `PO ${r.project.poNumber || '—'}` : (r.overhead ? `OH/${r.overhead}` : '—'), []);

  const runSearch = useCallback(async (term) => {
    setSearching(true);
    try {
      const { data } = await axios.get(`${API_BASE}/price-book/search?q=${encodeURIComponent(term)}`, authH());
      setRows(Array.isArray(data) ? data : []);
      setSearched(true);
    } catch (_) { setRows([]); setSearched(true); }
    finally { setSearching(false); }
  }, []);

  // debounced live search
  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    const term = q.trim();
    if (term.length < 2) { setRows([]); setSearched(false); return; }
    debRef.current = setTimeout(() => runSearch(term), 350);
    return () => clearTimeout(debRef.current);
  }, [q, runSearch]);
  const refreshSearch = () => { if (q.trim().length >= 2) runSearch(q.trim()); };

  // ---- click-to-sort (3 states per column) ----
  const [sortCol, setSortCol] = useState(() => localStorage.getItem('priceBook.sortCol') || null);
  const [sortDir, setSortDir] = useState(() => Number(localStorage.getItem('priceBook.sortDir')) || 1);
  useEffect(() => {
    if (sortCol) localStorage.setItem('priceBook.sortCol', sortCol);
    else localStorage.removeItem('priceBook.sortCol');
    localStorage.setItem('priceBook.sortDir', String(sortDir));
  }, [sortCol, sortDir]);
  const clickSort = (col) => {
    const first = col.type === 'text' ? 1 : -1; // text starts A→Z, numbers start high→low
    if (sortCol !== col.key) { setSortCol(col.key); setSortDir(first); return; }
    if (sortDir === first) { setSortDir(-first); return; }
    setSortCol(null); // third click: back to default
  };
  // ---- THE CATALOG: one row per PRODUCT ----
  // A named product (you gave it an alias) aggregates every member SKU across
  // vendors; a not-yet-named product is one distinct vendor item (name+SKU).
  // Purchases never appear as rows here — they live on the analytics page.
  const catalog = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const key = r.aliasId ? `alias:${r.aliasId}` : `prod:${keyOf(r)}`;
      let e = map.get(key);
      if (!e) { e = { key, named: !!r.aliasId, title: r.alias || r.name, rs: [] }; map.set(key, e); }
      e.rs.push(r);
    });
    return [...map.values()].map((e) => {
      const rs = e.rs.sort((a, b) => new Date(a.date) - new Date(b.date));
      const last = rs[rs.length - 1];
      // distinct SKUs (format-insensitive), each shown in its prettiest form
      const skuMap = new Map();
      rs.forEach((r) => {
        if (!r.sku) return;
        const n = normSku(r.sku);
        const cur = skuMap.get(n);
        if (!cur || (!/[^a-zA-Z0-9]/.test(cur) && /[^a-zA-Z0-9]/.test(r.sku))) skuMap.set(n, r.sku);
      });
      let skus = [...skuMap.values()];
      // old receipts may carry no item SKUs — a named product falls back to
      // the SKUs you put on its members
      if (!skus.length && e.named && last.aliasSkus && last.aliasSkus.length) skus = [...new Set(last.aliasSkus)];
      const vendors = [...new Set(rs.map((r) => r.store || '—'))];
      return {
        key: e.key, named: e.named, title: e.title,
        sku: skus.length ? (skus.length > 1 ? `${skus[0]} +${skus.length - 1}` : skus[0]) : '',
        vendors: vendors.length > 1 ? `${vendors[0]} +${vendors.length - 1}` : (vendors[0] || '—'),
        buys: rs.length,
        // per-piece when the product's members declare pack sizes
        latestUnit: Number(last.unitPrice || 0) / (Number(last.packOf) > 0 ? Number(last.packOf) : 1),
        avgUnit: rs.reduce((s, r) => s + Number(r.unitPrice || 0) / (Number(r.packOf) > 0 ? Number(r.packOf) : 1), 0) / rs.length,
        latestDate: new Date(last.date || 0).getTime(),
        lastRow: last, // carries receipt path / ids for the right-click actions
      };
    }).sort((a, b) => (b.named - a.named) || (b.latestDate - a.latestDate)); // named products first, then newest
  }, [rows]);

  const sorted = useMemo(() => {
    if (!sortCol) return catalog;
    const col = COLS.find((c) => c.key === sortCol);
    if (!col) return catalog;
    return [...catalog].sort((a, b) => {
      const va = a[sortCol], vb = b[sortCol];
      const cmp = col.type === 'text' ? String(va || '').localeCompare(String(vb || '')) : Number(va || 0) - Number(vb || 0);
      return cmp * sortDir;
    });
  }, [catalog, sortCol, sortDir]);
  const sortMark = (col) => sortCol === col.key ? (sortDir === 1 ? ' ▲' : ' ▼') : '';

  // ---- windowed list: show 30, load 30 more as the bottom scrolls into view
  const PAGE = 30;
  const [visibleCount, setVisibleCount] = useState(PAGE);
  useEffect(() => { setVisibleCount(PAGE); }, [rows, sortCol, sortDir]);
  const moreRef = useRef(null);
  useEffect(() => {
    const el = moreRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setVisibleCount((c) => c + PAGE);
    }, { rootMargin: '300px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [sorted.length, visibleCount, selected]);
  const shown = useMemo(() => sorted.slice(0, visibleCount), [sorted, visibleCount]);

  // click an Item Code in the catalog → copy it, confirm inline
  const [copiedCode, setCopiedCode] = useState('');
  const copyCode = (key, code) => (e) => {
    e.stopPropagation(); // don't open the analytics page
    const clean = String(code || '').split(' +')[0]; // multi-code cells copy the first
    try { navigator.clipboard && navigator.clipboard.writeText(clean); } catch (_) {}
    setCopiedCode(key);
    setTimeout(() => setCopiedCode(''), 1300);
  };

  // ---- right-click menu ----
  const [ctx, setCtx] = useState(null); // { x, y, row }
  const rowContext = (r) => (e) => { e.preventDefault(); setCtx({ x: e.clientX, y: e.clientY, row: r }); };
  const entryContext = (e) => (ev) => { ev.preventDefault(); setCtx({ x: ev.clientX, y: ev.clientY, row: e.lastRow }); };
  const seeReceipt = async (r) => {
    setCtx(null);
    if (!r.receiptImagePath) { alert('No receipt file is linked to this expense.'); return; }
    // Open the WINDOW immediately (inside the click) so popup blockers allow
    // it; the blob loads into it when the bytes arrive. A window, not a tab,
    // and displayed inline — never downloaded.
    const win = window.open('', '_blank', 'popup=yes,width=900,height=1100');
    try { win && win.document.write('<p style="font-family:sans-serif;color:#888;padding:24px">Loading receipt…</p>'); } catch (_) {}
    try {
      const resp = await axios.get(`${API_BASE}/expenses/receipt-file?path=${encodeURIComponent(r.receiptImagePath)}`,
        { ...authH(), responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      // Wrap the blob in our own dark page (black background) instead of the
      // browser's white default — PDF in a full-bleed embed, images centered.
      const isPdf = r.receiptImagePath.toLowerCase().endsWith('.pdf');
      // PDFs use the browser viewer (own zoom controls). Images get a simple
      // zoom/pan viewer: scroll wheel zooms at the cursor, drag pans,
      // double-click re-fits. Plain inline JS — no dependencies.
      const imgViewer = `<div id="w" style="width:100%;height:100%;overflow:hidden;cursor:grab"><img id="m" src="${url}" style="transform-origin:0 0;user-select:none;-webkit-user-drag:none"></div>
<button id="tgl" style="position:fixed;top:10px;right:10px;z-index:9;padding:6px 12px;border-radius:999px;border:1px solid #444;background:#1c1c22;color:#a5b4fc;font:600 12px sans-serif;cursor:pointer">Select text</button>
<script>
var s=1,fs=1,tx=0,ty=0,drag=null,mode='pan',m=document.getElementById('m'),w=document.getElementById('w'),t=document.getElementById('tgl');
function fit(){var r=w.getBoundingClientRect(),iw=m.naturalWidth||1,ih=m.naturalHeight||1;fs=Math.min(r.width/iw,r.height/ih);s=fs;tx=(r.width-iw*s)/2;ty=(r.height-ih*s)/2;ap();}
function ap(){m.style.transform='translate('+tx+'px,'+ty+'px) scale('+s+')';}
m.onload=fit;window.onresize=fit;
t.onclick=function(){mode=mode==='pan'?'sel':'pan';
  if(mode==='sel'){w.style.cursor='auto';m.style.userSelect='text';m.style.webkitUserSelect='text';t.textContent='Zoom / pan';t.style.color='#6ee7b7';}
  else{w.style.cursor='grab';m.style.userSelect='none';m.style.webkitUserSelect='none';t.textContent='Select text';t.style.color='#a5b4fc';}};
w.addEventListener('wheel',function(e){e.preventDefault();
  // Smooth, delta-proportional zoom: Magic Mouse streams many tiny events,
  // so the factor follows the actual scroll amount (clamped per event), and
  // total zoom stays between 60% of fit and 10x fit.
  var f=Math.exp(-e.deltaY*0.002);f=Math.max(0.85,Math.min(1.18,f));
  var ns=Math.min(fs*10,Math.max(fs*0.6,s*f));
  tx=e.clientX-(e.clientX-tx)*(ns/s);ty=e.clientY-(e.clientY-ty)*(ns/s);s=ns;ap();},{passive:false});
w.addEventListener('pointerdown',function(e){if(mode==='sel')return;drag={x:e.clientX-tx,y:e.clientY-ty};w.setPointerCapture(e.pointerId);w.style.cursor='grabbing';});
w.addEventListener('pointermove',function(e){if(!drag)return;tx=e.clientX-drag.x;ty=e.clientY-drag.y;ap();});
w.addEventListener('pointerup',function(){drag=null;if(mode==='pan')w.style.cursor='grab';});
w.addEventListener('dblclick',function(){if(mode==='pan')fit();});
</${'script'}>`;
      const html = `<!doctype html><html><head><title>Receipt — ${(r.store || '').replace(/</g, '')}</title>
        <style>html,body{margin:0;height:100%;background:#000;overflow:hidden}embed{border:0;width:100%;height:100%}</style></head>
        <body>${isPdf ? `<embed src="${url}" type="application/pdf">` : imgViewer}</body></html>`;
      const target = (win && !win.closed) ? win : window.open('', '_blank', 'popup=yes,width=900,height=1100');
      if (target) {
        target.document.open();
        target.document.write(html);
        target.document.close();
      }
      setTimeout(() => URL.revokeObjectURL(url), 120000);
    } catch (_) {
      try { win && win.close(); } catch (__) {}
      alert('Could not open the receipt file.');
    }
  };

  // ---- alias modal (create / edit a group) ----
  const [aliasM, setAliasM] = useState(null); // { id, namesText, members, candQ, cands, picks }
  const [aliasBusy, setAliasBusy] = useState(false);
  // click a SKU chip to copy it (to look the product up before aliasing)
  const [copiedSku, setCopiedSku] = useState('');
  const copySku = (key, sku) => (e) => {
    e.preventDefault(); e.stopPropagation(); // don't toggle the candidate checkbox
    try { navigator.clipboard && navigator.clipboard.writeText(sku); } catch (_) {}
    setCopiedSku(key);
    setTimeout(() => setCopiedSku(''), 1200);
  };
  const openAlias = async (r) => {
    setCtx(null);
    let list = [];
    try { const { data } = await axios.get(`${API_BASE}/item-aliases`, authH()); list = data || []; } catch (_) {}
    if (r.aliasId) {
      const g = list.find((x) => String(x._id) === String(r.aliasId));
      if (g) { setAliasM({ id: g._id, namesText: (g.names || []).join('\n'), members: g.members || [], candQ: '', cands: [], picks: [], list: [], joinId: '' }); return; }
    }
    setAliasM({
      id: null,
      namesText: '',
      members: [{ display: r.name, sku: r.sku || '', vendor: r.store || '', packOf: guessPack(r.name) }],
      candQ: '', cands: [], picks: [],
      list, joinId: '', // naming can also JOIN an existing product instead of creating a near-duplicate
    });
  };
  const findCands = async () => {
    const term = (aliasM?.candQ || '').trim();
    if (term.length < 2) return;
    try {
      const { data } = await axios.get(`${API_BASE}/price-book/search?q=${encodeURIComponent(term)}`, authH());
      const have = new Set(aliasM.members.map((m) => normName(m.display) + '|' + (m.sku || '')));
      const seen = new Map();
      (data || []).forEach((r) => {
        const key = normName(r.name) + '|' + (r.sku || '');
        if (have.has(key)) return;
        const cur = seen.get(key);
        if (cur) cur.count++;
        else seen.set(key, { display: r.name, sku: r.sku || '', vendor: r.store || '', count: 1 });
      });
      setAliasM((m) => ({ ...m, cands: [...seen.values()].slice(0, 12), picks: [] }));
    } catch (_) {}
  };
  const saveAlias = async () => {
    if (aliasBusy || !aliasM) return;
    setAliasBusy(true);
    try {
      if (!aliasM.id && aliasM.joinId) {
        // JOIN: fold these members into the chosen existing product.
        const g = (aliasM.list || []).find((x) => String(x._id) === String(aliasM.joinId));
        if (!g) throw new Error('Pick a product to join');
        await axios.put(`${API_BASE}/item-aliases/${g._id}`, { names: g.names, members: [...(g.members || []), ...aliasM.members] }, authH());
      } else {
        const names = aliasM.namesText.split('\n').map((s) => s.trim()).filter(Boolean);
        if (!names.length || !aliasM.members.length) { alert('Give the product a name and at least one member item.'); setAliasBusy(false); return; }
        const body = { names, members: aliasM.members };
        if (aliasM.id) await axios.put(`${API_BASE}/item-aliases/${aliasM.id}`, body, authH());
        else await axios.post(`${API_BASE}/item-aliases`, body, authH());
      }
      setAliasM(null);
      refreshSearch();
    } catch (err) { alert(err.response?.data?.error || err.message || 'Could not save.'); }
    finally { setAliasBusy(false); }
  };
  const deleteAlias = async () => {
    if (!aliasM?.id || !window.confirm('Delete this alias group? Items keep their vendor names.')) return;
    try {
      await axios.delete(`${API_BASE}/item-aliases/${aliasM.id}`, authH());
      setAliasM(null);
      refreshSearch();
    } catch (_) { alert('Could not delete the alias.'); }
  };

  // ---- Edit Items modal (fix extraction misreads — totals never change) ----
  const [itemsM, setItemsM] = useState(null); // { expenseId, store, amount, rows }
  const [itemsBusy, setItemsBusy] = useState(false);
  const openItems = async (r) => {
    setCtx(null);
    try {
      const { data: exp } = await axios.get(`${API_BASE}/expenses/one/${r.expenseId}`, authH());
      setItemsM({
        expenseId: r.expenseId, store: exp.store || '', amount: Number(exp.amount || 0),
        rows: (exp.items && exp.items.length)
          ? exp.items.map((it) => ({ name: it.name || '', sku: it.sku || '', quantity: it.quantity ?? 1, unitPrice: it.unitPrice ?? '', total: it.total ?? '' }))
          : [{ name: '', sku: '', quantity: 1, unitPrice: '', total: '' }],
      });
    } catch (_) { alert('Could not load that expense.'); }
  };
  const setItemsRow = (i, f, v) => setItemsM((m) => ({
    ...m,
    rows: m.rows.map((r, j) => {
      if (j !== i) return r;
      const next = { ...r, [f]: v };
      if (f === 'quantity' || f === 'unitPrice') {
        const qn = Number(f === 'quantity' ? v : next.quantity) || 0;
        const u = Number(f === 'unitPrice' ? v : next.unitPrice) || 0;
        if (qn && u) next.total = (Math.round(qn * u * 100) / 100).toString();
      }
      return next;
    }),
  }));
  const itemsSum = (itemsM?.rows || []).reduce((s, r) => s + (Number(r.total) || 0), 0);
  const saveItems = async () => {
    if (itemsBusy || !itemsM) return;
    setItemsBusy(true);
    try {
      await axios.put(`${API_BASE}/expenses/${itemsM.expenseId}/items`, {
        items: itemsM.rows.filter((r) => r.name.trim()).map((r) => ({
          name: r.name, sku: r.sku, quantity: Number(r.quantity) || 1,
          unitPrice: Number(r.unitPrice) || 0, total: Number(r.total) || 0,
        })),
      }, authH());
      setItemsM(null);
      refreshSearch();
    } catch (err) { alert(err.response?.data?.error || 'Could not save the items.'); }
    finally { setItemsBusy(false); }
  };

  // ---- set a SKU on a no-SKU item (backfills every matching receipt item) ----
  const [skuEdit, setSkuEdit] = useState(null); // null | '' (editing) | typing value
  const [skuBusy, setSkuBusy] = useState(false);
  const commitSku = async (name) => {
    const sku = String(skuEdit || '').trim();
    if (!sku || skuBusy) return;
    setSkuBusy(true);
    try {
      await axios.put(`${API_BASE}/price-book/set-sku`, { name, sku }, authH());
      setSkuEdit(null);
      setSelected(`${normName(name)}|${sku}`); // the item's identity key just changed
      refreshSearch();
    } catch (err) { alert(err.response?.data?.error || 'Could not set the SKU.'); }
    finally { setSkuBusy(false); }
  };

  // ---- analytics for one item (grouped by normalized name) ----
  // 'alias:<id>' selects the whole GROUP — the combined, cross-vendor view
  // ("what have I been paying for X, from anyone"); a name|sku key selects
  // one exact product.
  const isAliasView = typeof selected === 'string' && selected.startsWith('alias:');
  const detailRows = useMemo(() => {
    if (!selected) return [];
    const match = isAliasView
      ? (r) => String(r.aliasId || '') === selected.slice(6)
      : selected.startsWith('prod:')
        ? (r) => `prod:${keyOf(r)}` === selected
        : (r) => keyOf(r) === selected;
    return rows.filter(match).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [rows, selected, isAliasView]);

  // PER-PIECE lens: packs normalize to pieces (5-pack ÷ 5) wherever a member
  // declares its pack size; everything else divides by 1 and is unchanged.
  // The purchase log still shows package prices — the receipt's literal truth.
  const unitOf = useCallback((r) => Number(r.unitPrice || 0) / (Number(r.packOf) > 0 ? Number(r.packOf) : 1), []);
  const hasPacks = useMemo(() => detailRows.some((r) => Number(r.packOf) > 1), [detailRows]);

  const priceStats = useMemo(() => {
    if (!detailRows.length) return null;
    let lo = detailRows[0], hi = detailRows[0];
    detailRows.forEach((r) => {
      if (unitOf(r) < unitOf(lo)) lo = r;
      if (unitOf(r) > unitOf(hi)) hi = r;
    });
    const avg = detailRows.reduce((s, r) => s + unitOf(r), 0) / detailRows.length;
    const latest = detailRows[detailRows.length - 1];
    return { lo, hi, avg, latest };
  }, [detailRows, unitOf]);

  const vendorStats = useMemo(() => {
    const by = {};
    detailRows.forEach((r) => { const v = r.store || 'Unknown'; (by[v] = by[v] || []).push(r); });
    return Object.entries(by).map(([vendor, list]) => ({
      vendor,
      buys: list.length,
      avgUnit: list.reduce((s, r) => s + unitOf(r), 0) / list.length,
      avgTax: list.reduce((s, r) => s + Number(r.taxShare || 0) / (Number(r.packOf) > 0 ? Number(r.packOf) : 1), 0) / list.length,
      lastUnit: unitOf(list[list.length - 1]),
      first: list[0].date, last: list[list.length - 1].date,
    })).sort((a, b) => a.avgUnit - b.avgUnit);
  }, [detailRows, unitOf]);

  const chartData = useMemo(() => ({
    labels: detailRows.map((r) => fmtDate(r.date)),
    datasets: [
      { label: hasPacks ? 'Price per piece (ex-tax)' : 'Unit price (ex-tax)', data: detailRows.map((r) => unitOf(r)), borderColor: '#818cf8', backgroundColor: '#818cf8', tension: 0.25, pointRadius: 4 },
      { label: hasPacks ? 'Tax per piece' : 'Tax on item', data: detailRows.map((r) => Number(r.taxShare || 0) / (Number(r.packOf) > 0 ? Number(r.packOf) : 1)), borderColor: '#f59e0b', backgroundColor: '#f59e0b', tension: 0.25, pointRadius: 4 },
    ],
  }), [detailRows, unitOf, hasPacks]);

  const chartOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { labels: { color: '#d4d4d8' } },
      tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${money(c.parsed.y)}` } },
    },
    scales: {
      x: { ticks: { color: '#71717a' }, grid: { color: 'rgba(63,63,70,0.4)' } },
      y: { ticks: { color: '#71717a', callback: (v) => `$${v}` }, grid: { color: 'rgba(63,63,70,0.4)' } },
    },
  };

  // ==================== ANALYTICS VIEW ====================
  if (selected) {
    const displayName = isAliasView ? (detailRows[0]?.alias || 'Alias group') : (detailRows[0]?.name || selected);
    const aliasProducts = isAliasView ? new Set(detailRows.map((r) => keyOf(r))).size : 0;
    const aliasVendors = isAliasView ? new Set(detailRows.map((r) => r.store || '—')).size : 0;
    return (
      <div className="min-h-screen bg-gray-950 text-zinc-100 p-8 max-md:p-3 max-w-5xl mx-auto space-y-5">
        <button onClick={() => setSelected(null)} className="flex items-center gap-2 text-sm text-indigo-400 hover:text-indigo-300">
          <ArrowLeft className="w-4 h-4" /> Back to search
        </button>
        <div>
          <h1 className="text-2xl max-md:text-xl font-bold tracking-tight flex items-center gap-2.5">
            <TrendingUp className="w-6 h-6 text-indigo-400 shrink-0" /> {displayName}
          </h1>
          <p className="text-sm text-zinc-500 mt-1 flex items-center flex-wrap gap-2">
            {isAliasView ? (
              <span className="text-[11px] font-bold px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-300">
                Alias group · {aliasProducts} product{aliasProducts === 1 ? '' : 's'} · {aliasVendors} vendor{aliasVendors === 1 ? '' : 's'}
              </span>
            ) : prettySku(detailRows) ? (
              <span className="text-[11px] font-mono font-bold px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300">{codeKind(prettySku(detailRows))} {prettySku(detailRows)}</span>
            ) : skuEdit !== null ? (
              <span className="inline-flex items-center gap-1.5">
                <input autoFocus value={skuEdit} onChange={(e) => setSkuEdit(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitSku(displayName); if (e.key === 'Escape') setSkuEdit(null); }}
                  placeholder="Type the SKU…"
                  className="bg-gray-800 border border-sky-600/60 rounded px-2 py-0.5 text-xs font-mono text-white outline-none w-40" />
                <button onClick={() => commitSku(displayName)} disabled={skuBusy} className="text-[11px] font-bold text-sky-300 hover:text-sky-200 disabled:opacity-40">{skuBusy ? 'Saving…' : 'Save'}</button>
                <button onClick={() => setSkuEdit(null)} className="text-[11px] text-zinc-500 hover:text-zinc-300">Cancel</button>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5">
                <span className="text-[11px] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300">NO SKU</span>
                <button onClick={() => setSkuEdit('')} className="text-[11px] font-semibold text-sky-400 hover:text-sky-300" title="Backfills this SKU onto every receipt item with this exact name and no SKU">Set SKU</button>
              </span>
            )}
            <span>{detailRows.length} purchase{detailRows.length === 1 ? '' : 's'} on record — from your receipts only</span>
          </p>
        </div>

        {/* lowest / highest / average / latest — visible at once, no dot-hunting */}
        {priceStats && (
          <div className="grid grid-cols-4 max-md:grid-cols-2 gap-2.5">
            <div className="bg-gray-900 border border-emerald-700/40 rounded-2xl px-4 py-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-400">Lowest paid</p>
              <p className="text-xl font-bold text-emerald-300 mt-0.5">{money(unitOf(priceStats.lo))}{hasPacks && <span className="text-xs text-zinc-500"> /pc</span>}</p>
              <p className="text-[11px] text-zinc-500 truncate">{priceStats.lo.store || '—'} · {fmtDate(priceStats.lo.date)}</p>
            </div>
            <div className="bg-gray-900 border border-red-700/40 rounded-2xl px-4 py-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-red-400">Highest paid</p>
              <p className="text-xl font-bold text-red-300 mt-0.5">{money(unitOf(priceStats.hi))}{hasPacks && <span className="text-xs text-zinc-500"> /pc</span>}</p>
              <p className="text-[11px] text-zinc-500 truncate">{priceStats.hi.store || '—'} · {fmtDate(priceStats.hi.date)}</p>
            </div>
            <div className="bg-gray-900 border border-indigo-700/40 rounded-2xl px-4 py-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-400">Average</p>
              <p className="text-xl font-bold text-indigo-300 mt-0.5">{money(priceStats.avg)}{hasPacks && <span className="text-xs text-zinc-500"> /pc</span>}</p>
              <p className="text-[11px] text-zinc-500">across {detailRows.length} buys</p>
            </div>
            <div className="bg-gray-900 border border-gray-700/60 rounded-2xl px-4 py-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Latest</p>
              <p className="text-xl font-bold text-zinc-200 mt-0.5">{money(unitOf(priceStats.latest))}{hasPacks && <span className="text-xs text-zinc-500"> /pc</span>}</p>
              <p className="text-[11px] text-zinc-500 truncate">{priceStats.latest.store || '—'} · {fmtDate(priceStats.latest.date)}</p>
            </div>
          </div>
        )}

        <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-6 max-md:p-3 shadow-xl">
          <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-400 mb-4">Price over time</h2>
          <div style={{ height: 300 }}>
            <Line data={chartData} options={chartOpts} />
          </div>
        </div>

        <div className="bg-gray-900 rounded-2xl border border-gray-800/50 overflow-hidden shadow-xl">
          <div className="p-5 border-b border-gray-800"><h2 className="text-sm font-bold uppercase tracking-wider text-zinc-400">Average price by vendor</h2></div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-800/60 text-sm">
              <thead className="bg-gray-950/30 text-xs text-zinc-500 uppercase">
                <tr>
                  <th className="px-5 py-3 text-left">Vendor</th>
                  <th className="px-5 py-3 text-right">Buys</th>
                  <th className="px-5 py-3 text-right">{hasPacks ? 'Avg /pc (ex-tax)' : 'Avg unit (ex-tax)'}</th>
                  <th className="px-5 py-3 text-right">Avg tax</th>
                  <th className="px-5 py-3 text-right">{hasPacks ? 'Latest /pc' : 'Latest unit'}</th>
                  <th className="px-5 py-3 text-right">First → Last</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/40">
                {vendorStats.map((v) => (
                  <tr key={v.vendor}>
                    <td className="px-5 py-3 text-zinc-200">{v.vendor}</td>
                    <td className="px-5 py-3 text-right text-zinc-400">{v.buys}</td>
                    <td className="px-5 py-3 text-right text-green-400 font-semibold">{money(v.avgUnit)}</td>
                    <td className="px-5 py-3 text-right text-amber-300">{money(v.avgTax)}</td>
                    <td className="px-5 py-3 text-right text-zinc-200">{money(v.lastUnit)}</td>
                    <td className="px-5 py-3 text-right text-zinc-500 whitespace-nowrap">{fmtDate(v.first)} → {fmtDate(v.last)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-gray-900 rounded-2xl border border-gray-800/50 overflow-hidden shadow-xl">
          <div className="p-5 border-b border-gray-800"><h2 className="text-sm font-bold uppercase tracking-wider text-zinc-400">Every purchase</h2></div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-800/60 text-sm">
              <thead className="bg-gray-950/30 text-xs text-zinc-500 uppercase">
                <tr>
                  <th className="px-5 py-3 text-left">Date</th>
                  <th className="px-5 py-3 text-left">Vendor</th>
                  <th className="px-5 py-3 text-right">Qty</th>
                  <th className="px-5 py-3 text-right">Unit (ex-tax)</th>
                  {hasPacks && <th className="px-5 py-3 text-right">Per piece</th>}
                  <th className="px-5 py-3 text-right">Tax</th>
                  <th className="px-5 py-3 text-right">Total w/ tax</th>
                  <th className="px-5 py-3 text-left">For</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/40">
                {[...detailRows].reverse().map((r, i) => (
                  <tr key={i} onContextMenu={rowContext(r)} className="cursor-context-menu">
                    <td className="px-5 py-3 text-zinc-400 whitespace-nowrap">{fmtDate(r.date)}</td>
                    <td className="px-5 py-3 text-zinc-200">{r.store || '—'}</td>
                    <td className="px-5 py-3 text-right text-zinc-400">{r.quantity ?? '—'}{Number(r.packOf) > 1 ? <span className="text-zinc-600 text-xs"> ×{r.packOf}/pk</span> : null}</td>
                    <td className="px-5 py-3 text-right text-green-400 font-semibold">{money(r.unitPrice)}</td>
                    {hasPacks && <td className="px-5 py-3 text-right text-indigo-300 font-semibold">{money(unitOf(r))}</td>}
                    <td className="px-5 py-3 text-right text-amber-300">{money(r.taxShare)}</td>
                    <td className="px-5 py-3 text-right text-zinc-200">{money(Number(r.lineTotal || 0) + Number(r.taxShare || 0))}</td>
                    <td className="px-5 py-3 text-zinc-500 whitespace-nowrap">{forLabel(r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {ctxMenu()}
        {modals()}
      </div>
    );
  }

  // ---- shared: context menu + modals (rendered in both views) ----
  function ctxMenu() {
    if (!ctx) return null;
    return (
      <>
        <div className="fixed inset-0 z-[80]" onClick={() => setCtx(null)} onContextMenu={(e) => { e.preventDefault(); setCtx(null); }} />
        <div className="fixed z-[90] bg-gray-900 border border-gray-700 rounded-xl shadow-2xl py-1.5 min-w-[200px] text-sm"
          style={{ left: Math.min(ctx.x, (window.innerWidth || 800) - 220), top: Math.min(ctx.y, (window.innerHeight || 600) - 130) }}>
          <button onClick={() => seeReceipt(ctx.row)} className="w-full px-4 py-2 text-left text-zinc-200 hover:bg-gray-800">See Original Receipt</button>
          <button onClick={() => openAlias(ctx.row)} className="w-full px-4 py-2 text-left text-zinc-200 hover:bg-gray-800">
            {ctx.row.alias ? `Rename "${ctx.row.alias}"…` : 'Name this product…'}
          </button>
          <button onClick={() => openItems(ctx.row)} className="w-full px-4 py-2 text-left text-zinc-200 hover:bg-gray-800">Edit Items…</button>
        </div>
      </>
    );
  }

  function modals() {
    return (
      <>
        {/* ---- alias group modal ---- */}
        {aliasM && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[110] p-4 backdrop-blur-sm">
            <div className="bg-gray-900 rounded-2xl p-7 w-full max-w-xl border border-gray-700 shadow-2xl max-h-[90dvh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-1 text-white">{aliasM.id ? 'Edit Alias' : 'Add Alias'}</h2>
              <p className="text-zinc-500 text-sm mb-5">Your trade names for one real item. Applies to every past and future purchase of its members — search any name to see them all.</p>

              {!aliasM.id && (aliasM.list || []).length > 0 && (
                <div className="mb-4">
                  <label className="block text-zinc-400 text-sm mb-2">Join an existing product <span className="text-zinc-600">(instead of creating a near-duplicate)</span></label>
                  <select value={aliasM.joinId} onChange={(e) => setAliasM((m) => ({ ...m, joinId: e.target.value }))}
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-indigo-500 outline-none">
                    <option value="">— create a new product —</option>
                    {aliasM.list.map((g) => <option key={g._id} value={g._id}>{(g.names || [])[0]}</option>)}
                  </select>
                  {aliasM.joinId && (() => {
                    const g = aliasM.list.find((x) => String(x._id) === String(aliasM.joinId));
                    return g ? <p className="text-xs text-indigo-300 mt-1.5">Joins "{g.names[0]}" — currently {g.members.length} member{g.members.length === 1 ? '' : 's'}; your {aliasM.members.length} below will be added.</p> : null;
                  })()}
                </div>
              )}
              {!aliasM.joinId && (
                <>
                  <label className="block text-zinc-400 text-sm mb-2">Your names (one per line)</label>
                  <textarea rows={2} value={aliasM.namesText} onChange={(e) => setAliasM((m) => ({ ...m, namesText: e.target.value }))}
                    placeholder={'1/2" Raintight EMT Connector\n1/2 RT. Con.'}
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-indigo-500 outline-none mb-4" />
                </>
              )}

              <p className="text-zinc-400 text-sm mb-1">Member items <span className="text-zinc-600">(what vendors call it)</span></p>
              <p className="text-[11px] text-amber-300/80 mb-2">A member with a SKU matches by SKU only — vendor names can be ambiguous (Home Depot's EMT1/2"RTCOM covers connectors AND couplings in several pack sizes; only the SKU tells them apart). Check the receipt before trusting a name.</p>
              <div className="space-y-1.5 mb-3">
                {aliasM.members.map((m, i) => (
                  <div key={i} className="flex items-center gap-2.5 bg-gray-950/60 border border-gray-800/70 rounded-xl px-3.5 py-2.5">
                    <span className="text-sm text-zinc-200 flex-1 min-w-0 break-words">{m.display}</span>
                    {m.sku ? (
                      <button onClick={copySku(`m${i}`, m.sku)} title="Click to copy this SKU"
                        className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300 hover:bg-sky-500/30 shrink-0">
                        {copiedSku === `m${i}` ? '✓ copied' : `${codeKind(m.sku)} ${m.sku}`}
                      </button>
                    ) : aliasM.skuEditIdx === i ? (
                      <input autoFocus placeholder="SKU…" defaultValue=""
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { const v = e.target.value.trim(); setAliasM((am) => ({ ...am, skuEditIdx: null, members: v ? am.members.map((x, j) => j === i ? { ...x, sku: v } : x) : am.members })); }
                          if (e.key === 'Escape') setAliasM((am) => ({ ...am, skuEditIdx: null }));
                        }}
                        onBlur={(e) => { const v = e.target.value.trim(); setAliasM((am) => ({ ...am, skuEditIdx: null, members: v ? am.members.map((x, j) => j === i ? { ...x, sku: v } : x) : am.members })); }}
                        className="bg-gray-800 border border-sky-600/60 rounded px-2 py-0.5 text-[11px] font-mono text-white outline-none w-28 shrink-0" />
                    ) : (
                      <button onClick={() => setAliasM((am) => ({ ...am, skuEditIdx: i }))}
                        title="No SKU — this member matches by name, which can be ambiguous. Click to type the SKU (switches it to exact SKU matching)."
                        className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 hover:bg-amber-500/30 shrink-0">
                        no SKU · add?
                      </button>
                    )}
                    <span className="text-[11px] text-zinc-500 shrink-0">{m.vendor || ''}</span>
                    {/* how this vendor sells it: pieces per pack, or priced per foot
                        (field = feet per stick) — per-piece pricing uses this */}
                    <span className="flex items-center gap-1 shrink-0"
                      title={m.unitMode === 'ft' ? 'Priced per foot — field is feet per stick (10ft stick = 10): $0.89/ft shows as $8.90/stick' : 'Pieces per package (a 5-pack = 5)'}>
                      <button
                        onClick={() => setAliasM((am) => ({ ...am, members: am.members.map((x, j) => j === i
                          ? (x.unitMode === 'ft'
                              ? { ...x, unitMode: 'pack', packOf: guessPack(x.display) }
                              : { ...x, unitMode: 'ft', packOf: 10 })
                          : x) }))}
                        className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${m.unitMode === 'ft' ? 'bg-cyan-500/20 text-cyan-300' : 'bg-gray-800 text-zinc-500 hover:text-zinc-300'}`}
                      >
                        {m.unitMode === 'ft' ? '/ft' : 'pack'}
                      </button>
                      <span className="text-[10px] text-zinc-600">×</span>
                      <input inputMode="decimal" value={m.packOf ?? 1}
                        onChange={(e) => { const v = e.target.value.replace(/[^0-9.]/g, ''); setAliasM((am) => ({ ...am, members: am.members.map((x, j) => j === i ? { ...x, packOf: v } : x) })); }}
                        className="w-11 bg-gray-800 border border-gray-700 rounded px-1.5 py-0.5 text-[11px] text-center text-white outline-none focus:border-indigo-500" />
                      <span className="text-[10px] text-zinc-600">{m.unitMode === 'ft' ? 'ft/stick' : '/pk'}</span>
                    </span>
                    <button onClick={() => setAliasM((am) => ({ ...am, members: am.members.filter((_, j) => j !== i) }))} className="text-zinc-600 hover:text-red-400">×</button>
                  </div>
                ))}
                {aliasM.members.length === 0 && <p className="text-xs text-amber-300">No members — add at least one below.</p>}
              </div>

              {/* find more members across vendors */}
              <div className="bg-gray-950/50 border border-gray-800/70 rounded-xl p-3.5 mb-5">
                <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-2">Find more members (other vendors' names for it)</p>
                <div className="flex gap-2 mb-2">
                  <input value={aliasM.candQ} onChange={(e) => setAliasM((m) => ({ ...m, candQ: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') findCands(); }}
                    placeholder="e.g. RTCON, raintight…"
                    className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-indigo-500 outline-none" />
                  <button onClick={findCands} className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-sm font-semibold text-white">Search</button>
                </div>
                {aliasM.cands.map((c, i) => (
                  <label key={i} className="flex items-center gap-2.5 py-1.5 cursor-pointer">
                    <input type="checkbox" checked={aliasM.picks.includes(i)}
                      onChange={() => setAliasM((m) => ({ ...m, picks: m.picks.includes(i) ? m.picks.filter((x) => x !== i) : [...m.picks, i] }))} />
                    <span className="text-sm text-zinc-300 flex-1 min-w-0 break-words">{c.display}</span>
                    {c.sku ? (
                      <button onClick={copySku(`c${i}`, c.sku)} title="Click to copy this SKU"
                        className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300 hover:bg-sky-500/30 shrink-0">
                        {copiedSku === `c${i}` ? '✓ copied' : `${codeKind(c.sku)} ${c.sku}`}
                      </button>
                    ) : (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 shrink-0">no SKU</span>
                    )}
                    <span className="text-[11px] text-zinc-500 shrink-0">{c.vendor} · ×{c.count}</span>
                  </label>
                ))}
                {aliasM.picks.length > 0 && (
                  <button onClick={() => setAliasM((m) => ({ ...m, members: [...m.members, ...m.picks.map((i) => ({ ...m.cands[i], packOf: guessPack(m.cands[i].display) }))], cands: m.cands.filter((_, i) => !m.picks.includes(i)), picks: [] }))}
                    className="mt-2 text-xs font-semibold text-emerald-400 hover:text-emerald-300">+ Add selected to members</button>
                )}
              </div>

              <div className="flex gap-3">
                {aliasM.id && <button onClick={deleteAlias} className="px-4 py-3 bg-red-900/40 hover:bg-red-900/60 text-red-300 rounded-xl font-medium">Delete</button>}
                <button onClick={() => setAliasM(null)} className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 text-zinc-300 rounded-xl font-medium">Cancel</button>
                <button onClick={saveAlias} disabled={aliasBusy} className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white rounded-xl font-bold">{aliasBusy ? 'Saving…' : 'Save Alias'}</button>
              </div>
            </div>
          </div>
        )}

        {/* ---- edit items modal (totals never change) ---- */}
        {itemsM && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[110] p-4 backdrop-blur-sm">
            <div className="bg-gray-900 rounded-2xl p-7 w-full max-w-2xl border border-gray-700 shadow-2xl max-h-[90dvh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-1 text-white">Edit Items</h2>
              <p className="text-zinc-500 text-sm mb-5">{itemsM.store || 'Receipt'} · booked subtotal {money(itemsM.amount)} — fix misreads (a wrong line total skews that item's tax share). Booked totals never change here.</p>
              <div className="space-y-2 mb-3">
                {itemsM.rows.map((r, i) => (
                  <div key={i} className="grid grid-cols-[1fr_110px_60px_90px_90px_28px] max-md:grid-cols-2 gap-2 items-center">
                    <input placeholder="Item name" value={r.name} onChange={(e) => setItemsRow(i, 'name', e.target.value)} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-indigo-500 outline-none max-md:col-span-2" />
                    <input placeholder="SKU" value={r.sku} onChange={(e) => setItemsRow(i, 'sku', e.target.value)} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-indigo-500 outline-none" />
                    <input placeholder="Qty" inputMode="decimal" value={r.quantity} onChange={(e) => setItemsRow(i, 'quantity', e.target.value.replace(/[^0-9.]/g, ''))} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-indigo-500 outline-none" />
                    <input placeholder="Unit" inputMode="decimal" value={r.unitPrice} onChange={(e) => setItemsRow(i, 'unitPrice', e.target.value.replace(/[^0-9.]/g, ''))} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-indigo-500 outline-none" />
                    <input placeholder="Total" inputMode="decimal" value={r.total} onChange={(e) => setItemsRow(i, 'total', e.target.value.replace(/[^0-9.]/g, ''))} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-indigo-500 outline-none" />
                    <button onClick={() => setItemsM((m) => ({ ...m, rows: m.rows.filter((_, j) => j !== i) }))} className="text-zinc-600 hover:text-red-400 text-lg">×</button>
                  </div>
                ))}
              </div>
              <button onClick={() => setItemsM((m) => ({ ...m, rows: [...m.rows, { name: '', sku: '', quantity: 1, unitPrice: '', total: '' }] }))} className="text-xs text-indigo-400 hover:text-indigo-300 font-semibold mb-4">+ Add item</button>
              <div className={`rounded-xl px-4 py-3 mb-6 text-sm border ${Math.abs(itemsSum - itemsM.amount) <= 0.05 ? 'bg-emerald-500/10 border-emerald-600/40 text-emerald-300' : 'bg-amber-500/10 border-amber-600/40 text-amber-300'}`}>
                Items sum to <span className="font-bold">{money(itemsSum)}</span> · booked subtotal {money(itemsM.amount)}
                {Math.abs(itemsSum - itemsM.amount) <= 0.05 ? ' — reconciles ✓' : ` — off by ${money(Math.abs(itemsSum - itemsM.amount))}`}
              </div>
              <div className="flex gap-3">
                <button onClick={() => setItemsM(null)} className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 text-zinc-300 rounded-xl font-medium">Cancel</button>
                <button onClick={saveItems} disabled={itemsBusy || !itemsM.rows.some((r) => r.name.trim())} className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white rounded-xl font-bold">{itemsBusy ? 'Saving…' : 'Save Items'}</button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // ==================== SEARCH VIEW ====================
  return (
    <div className="min-h-screen bg-gray-950 text-zinc-100 p-8 max-md:p-3 max-w-5xl mx-auto space-y-5">
      <div>
        <h1 className="text-2xl max-md:text-xl font-bold tracking-tight">Price Book</h1>
        <p className="text-sm text-zinc-500 mt-1">What you actually paid — every line item from every receipt. Click an item for history; right-click for receipt, alias, or corrections; click a column to sort.</p>
      </div>

      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder='Search items or your aliases… e.g. 1" PVC sch 40, TR outlet'
          className="w-full bg-gray-900 border border-gray-800 rounded-xl pl-10 pr-10 py-3 text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-indigo-600/60"
        />
        {q && (
          <button
            onClick={() => setQ('')}
            title="Clear search"
            className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-zinc-500 hover:text-zinc-200 hover:bg-gray-800"
          >
            ×
          </button>
        )}
      </div>

      {searching && <p className="text-sm text-zinc-500 animate-pulse">Searching your receipts…</p>}
      {!searching && searched && rows.length === 0 && (
        <p className="text-sm text-zinc-500">No purchases match — items appear here as receipts are uploaded and parsed.</p>
      )}

      {sorted.length > 0 && (
        <div className="bg-gray-900 rounded-2xl border border-gray-800/50 overflow-hidden shadow-xl">
          <div className="overflow-x-auto max-md:hidden">
            <table className="min-w-full divide-y divide-gray-800/60 text-sm">
              <thead className="bg-gray-950/30 text-xs text-zinc-500 uppercase select-none">
                <tr>
                  {COLS.map((c) => (
                    <th key={c.key} onClick={() => clickSort(c)} title="Click to sort"
                      className={`px-5 py-3 cursor-pointer hover:text-zinc-300 whitespace-nowrap ${c.right ? 'text-right' : 'text-left'} ${sortCol === c.key ? 'text-indigo-300' : ''}`}>
                      {c.label}{sortMark(c)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/40">
                {shown.map((e) => (
                  <tr key={e.key} onClick={() => setSelected(e.key)} onContextMenu={entryContext(e)} title="Click for price history · right-click for actions"
                    className="hover:bg-gray-800/50 transition-colors cursor-pointer">
                    <td className="px-5 py-3 text-zinc-200 max-w-[340px]">
                      <span className="line-clamp-2">{e.title}</span>
                      {!e.named && <span className="inline-block mt-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300">not named yet</span>}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap font-mono text-xs" onClick={e.sku ? copyCode(e.key, e.sku) : undefined} title={e.sku ? `${codeKind(e.sku)} · ${e.vendors} — click to copy` : ''}>
                      {copiedCode === e.key
                        ? <span className="text-emerald-400 font-sans font-semibold">✓ Copied to clipboard!</span>
                        : (e.sku ? <span className="text-zinc-400 hover:text-sky-300 cursor-pointer border-b border-dotted border-zinc-700">{e.sku}</span> : <span className="text-zinc-700">—</span>)}
                    </td>
                    <td className="px-5 py-3 text-zinc-400 whitespace-nowrap">{e.vendors}</td>
                    <td className="px-5 py-3 text-right text-zinc-400">{e.buys}</td>
                    <td className="px-5 py-3 text-right text-green-400 font-semibold">{money(e.latestUnit)}</td>
                    <td className="px-5 py-3 text-right text-zinc-300">{money(e.avgUnit)}</td>
                    <td className="px-5 py-3 text-zinc-500 whitespace-nowrap">{fmtDate(e.latestDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* mobile cards */}
          <div className="md:hidden divide-y divide-gray-800/40">
            {shown.map((e) => (
              <div key={e.key} onClick={() => setSelected(e.key)} onContextMenu={entryContext(e)} className="px-4 py-3 cursor-pointer active:bg-gray-800/40">
                <div className="flex justify-between items-start gap-2">
                  <span className="text-sm text-zinc-200 break-words min-w-0 flex-1">{e.title}{!e.named && <span className="ml-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 align-middle">not named</span>}</span>
                  <span className="text-green-400 font-bold text-sm shrink-0">{money(e.latestUnit)}</span>
                </div>
                <div className="flex justify-between items-center mt-1.5 text-xs text-zinc-500">
                  <span>{e.vendors} · ×{e.buys}{e.sku ? ` · ${e.sku}` : ''}</span>
                  <span>{fmtDate(e.latestDate)}</span>
                </div>
              </div>
            ))}
          </div>
          {/* scroll sentinel: nearing it loads the next 30 */}
          {visibleCount < sorted.length && (
            <div ref={moreRef} className="py-4 text-center text-xs text-zinc-600">
              Showing {shown.length} of {sorted.length} products — scroll for more…
            </div>
          )}
        </div>
      )}
      {ctxMenu()}
      {modals()}
    </div>
  );
}

export default PriceBook;
